import { Injectable } from '@angular/core';
import PouchDB from 'pouchdb-browser';
import { DateTime } from 'luxon';

import { DeferredPromise } from '@cf-shared/shared-util';
import { isPouchConflict, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { Food } from '@cf-shared/data-model/food';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { getLocalAdapterOpts } from './local-database-engine';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import {
  makeLocalAdapterFaultReporter, makeLocalCorruptionReporter,
} from './pouchdb-adapter-capacitor-sqlite/local-corruption-reporter';
import { PubSubService } from 'services/pubsub.service';
import { ConnectionState, connectionStateChangedEvent } from 'services/online-state.service';
import { isTransportFailureAfterRetries } from 'services/cloud-functions-retry';
import { isOfflineOrTransientNetworkError } from 'services/error-patterns';
import {
  decideUnpublishReplayFate, queuedItemAgeDays, readAttemptCount, readQueuedAtIso,
  shouldDropUnpublishItem, UnpublishReplayFate,
} from 'services/food-unpublish-outbox-rules';

// df-efc66c4a2f6e (internal ticket #941) — a durable record of shared food
// copies this device still owes the server a removal for.
//
// Deleting a food deletes the user's own copy and then asks the server to take
// the shared copy out of the global, searchable food database. Before this, that
// second step was tried exactly once: `unpublishFood` is a resilient callable,
// so it retried for up to a few minutes and then gave up for good. A food
// deleted while offline — an ordinary thing to do in an offline-first app — was
// therefore left in `global_foods` for ever, where it kept turning up in the
// deleting user's own food search and in everybody else's.
//
// Nothing else could ever ask again. The local food document that might have
// carried the obligation was deleted a moment earlier, and no server-side sweep
// reconciles `global_foods` against users' own foods (`syncFoodSources` only
// ever touches the `food/$OFF-…` id range).
//
// So the obligation is written down here instead, and replayed when the
// connection comes back and after the next login. The shape is deliberately the
// same as `image-upload-outbox.service.ts`, which is the one mechanism of this
// kind in the app that has been shipped and debugged: a dedicated local-only
// PouchDB, a single-flight drain subscribed to the connectivity event, and a
// pure give-up rule in a separate file. See design-docs/sync-integrity.md,
// "The food unpublish outbox".

/**
 * One shared food copy still owed a removal.
 *
 * The doc `_id` is the food's own id, which makes a second delete of the same
 * food a harmless conflict rather than a duplicate entry.
 *
 * **The food's contents are deliberately NOT stored.** `unpublishFood_impl`
 * (`server/functions/src/publish-food.ts`) reads `food._id` and nothing else —
 * it re-derives ownership itself from an HMAC of the user id and that id — so
 * the id is the whole request. Keeping a copy of a food the user has just
 * deleted, on the device, until a reconnect happens to arrive, would be storing
 * their data for no purpose at all.
 *
 * There is no `adminMode` field. The food editor's officers-only mode was removed
 * by `#5679`, so every delete this outbox can ever queue is an ordinary user
 * deleting their own food, and the replay sends `adminMode: false` like the
 * delete itself does. The server still accepts the flag, guarded by `AuthRole.Dev`,
 * for app builds already in the field.
 */
interface UnpublishOutboxDoc {
  _id: string;
  _rev?: string;
  /** ISO timestamp, drives oldest-first drain order and the age cap */
  createdAt_utc: string;
  /** drain attempts that reached the server; a drain skipped offline does not count */
  attempts: number;
  /** carried only so a dropped item's report says what kind of food was left published */
  isRecipe: boolean;
}

/**
 * The outbox's local PouchDB name.
 *
 * Exported because the sign-out teardown has to delete it explicitly: this
 * database does NOT go through `DatabaseWrapperProvider`, so the SQLite half of
 * the wipe — which is handed a list of names rather than enumerating — would
 * never see it. That is the hole #5269 closed for the image outbox, and
 * `reload-teardown.test.ts` cross-checks this constant so it cannot re-open here.
 */
export const FOOD_UNPUBLISH_OUTBOX_DB_NAME = 'food_unpublish_outbox';

@Injectable({ providedIn: 'root' })
export class FoodUnpublishOutboxService {
  // opened lazily on first use, so an empty outbox costs nothing on the
  // boot-critical path
  private db: PouchDB.Database<UnpublishOutboxDoc> | null = null;

  // single-flight guard: a second drain trigger while one is running just
  // awaits the running one, the same pattern as the image outbox
  private drainInProgress: DeferredPromise<void> | null = null;

  constructor(
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
    private pubsub: PubSubService,
  ) {
    // drain whenever connectivity returns. the subscription lives for the app's
    // lifetime (root singleton), so no teardown is needed.
    this.pubsub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      if (state === ConnectionState.Online) void this.drainAsync();
    });
  }

  /** Open (once) and return the outbox DB. */
  private getDb(): PouchDB.Database<UnpublishOutboxDoc> {
    if (!this.db) {
      this.db = new PouchDB<UnpublishOutboxDoc>(FOOD_UNPUBLISH_OUTBOX_DB_NAME, {
        auto_compaction: true,
        ...getLocalAdapterOpts(
          makeLocalCorruptionReporter(this.logging, 'food-unpublish-outbox'),
          makeLocalAdapterFaultReporter(this.logging, 'food-unpublish-outbox'),
        ),
      });
    }
    return this.db;
  }

  /**
   * Remember that the server still owes us the removal of this food's shared
   * copy, so the ask can be repeated when the connection comes back.
   *
   * Never throws: the caller has already deleted the user's own copy and is on
   * its way to closing the editor, so a failure here must not derail that. It
   * is reported instead, because a failure means the obligation is lost exactly
   * the way it was lost before this outbox existed.
   */
  async enqueueAsync(food: Food, isRecipe: boolean): Promise<void> {
    try {
      await this.getDb().put({
        _id: food._id,
        createdAt_utc: DateTime.utc().toISO(),
        attempts: 0,
        isRecipe,
      });
    } catch (err) {
      // expected: a pouch conflict when this food is already queued — deleting
      // the same food twice is the user's own re-try, and the queued item
      // already says everything this one would
      if (isPouchConflict(err)) {
        console.log('FoodUnpublishOutbox: this food is already queued for unpublish', food._id);
        return;
      }
      this.logging.trackSilentProblem('food-unpublish.enqueue-failed', {
        foodId: food._id,
        error: describeError(err),
      });
    }
  }

  /**
   * Ask the server again for every queued removal, oldest first. Serialized:
   * concurrent triggers (a reconnect racing the post-login kick) await the
   * drain already in flight instead of sending everything twice.
   */
  async drainAsync(): Promise<void> {
    // if no user is signed in yet, skip: the boot-time reachability probe
    // publishes Online BEFORE auth completes, and a drain then would call the
    // cloud function with no auth token — every queued item would fail with an
    // auth error, which is not offline-classified and would wrongly burn the
    // give-up counter. the post-login kick re-runs the drain once auth is ready.
    if (!this.auth.user) {
      return;
    }
    if (this.drainInProgress != null) {
      return this.drainInProgress;
    }
    const deferred = DeferredPromise.defer<void>();
    this.drainInProgress = deferred;
    try {
      await this.drainInnerAsync();
    } catch (err) {
      // drain-machinery failure (the outbox DB itself failed to read, say) is
      // unexpected — surface it; the queued items are untouched and the next
      // trigger retries. per-item failures are handled inside.
      this.logging.trackSilentProblem('food-unpublish.drain-failed', {
        name: err instanceof Error ? err.name : undefined,
        message: describeError(err),
      });
    } finally {
      deferred.resolve();
      this.drainInProgress = null;
    }
  }

  /** One sequential pass over every queued removal. Only ever called by
   * `drainAsync`, under its single-flight guard. */
  private async drainInnerAsync(): Promise<void> {
    const db = this.getDb();
    const all = await db.allDocs({ include_docs: true });
    const docs = all.rows
      .map((row) => row.doc)
      .filter((doc): doc is UnpublishOutboxDoc & PouchDB.Core.ExistingDocument<UnpublishOutboxDoc> => doc != null)
      // oldest first: the food that has been left published longest goes first.
      // A document whose timestamp cannot be read sorts LAST, behind every item
      // whose age is known -- and it is compared through the normaliser rather
      // than directly, because one damaged document in a raw `localeCompare`
      // would throw HERE, before the loop, and disable the whole drain for good.
      .sort((a, b) => sortKeyOf(a).localeCompare(sortKeyOf(b)));

    for (const doc of docs) {
      // both fields are read through a normaliser rather than trusted: a damaged or
      // old-shaped value in EITHER of them makes the item un-droppable, and in the
      // attempt count's case silently so. See the two rules for what each answers.
      const ageDays = queuedItemAgeDays(doc.createdAt_utc, DateTime.utc());
      doc.attempts = readAttemptCount(doc.attempts);
      // give-up rule first: an item already past the caps is dropped, not retried.
      // An unreadable timestamp is passed as age 0, so such an item can only ever
      // be dropped by the attempt cap -- never thrown away for being old, which
      // is a thing we would not actually know about it.
      if (shouldDropUnpublishItem({ attempts: doc.attempts, ageDays: ageDays ?? 0 })) {
        await this.dropItemAsync(doc, ageDays, 'the attempt or age cap was reached');
        continue;
      }
      await this.replayOneAsync(doc, ageDays);
    }
  }

  /**
   * Ask the server once more to remove one queued food, and settle what that
   * answer means for the item.
   *
   * **This must never throw.** It is the body of the drain's item loop, and an
   * escaping error would abandon every remaining queued removal.
   */
  private async replayOneAsync(
    doc: UnpublishOutboxDoc & PouchDB.Core.ExistingDocument<UnpublishOutboxDoc>,
    ageDays: number | undefined,
  ): Promise<void> {
    let removed = false;
    let serverWasReached = true;
    let failure: string | undefined;
    try {
      // the server reads `_id` and nothing else out of this food — see the
      // UnpublishOutboxDoc comment on why no food body is kept here
      const result = await this.cloudFunctions.unpublishFoodAsync({
        food: { _id: doc._id } as Food,
        // false, like the delete that queued this one: the editor's officers-only
        // mode was removed by #5679, so every item here is a user's own food
        adminMode: false,
      });
      removed = !!result.ok;
      failure = result.error;
    } catch (err) {
      // `describeErrorForLog` rather than `describeError`, because `failure` only
      // ever reaches the console line at the end of this method — and a stack, the
      // hidden fields and the `cause` chain are exactly what a future debugger
      // reading that line wants. Nothing here crosses the wire.
      failure = describeErrorForLog(err);
      // two shapes mean the same thing: the call's own retries ran out against a
      // dead connection, and a bare fetch failure. Either way nothing was
      // learned about the food, so the item must not burn an attempt.
      serverWasReached = !isTransportFailureAfterRetries(err) && !isOfflineOrTransientNetworkError(err);
    }

    const fate = decideUnpublishReplayFate({ removed, serverWasReached });
    if (fate === UnpublishReplayFate.Done) {
      await this.removeItemAsync(doc);
      // an automatic repair ran and succeeded, which is the info tier: the
      // shared copy this device owed a removal for is out of the global food
      // database, and nobody has to act (#4382)
      this.logging.trackSilentInfo('food-unpublish.replayed', {
        foodId: doc._id,
        isRecipe: doc.isRecipe,
        attempts: doc.attempts,
        ageDays: ageDays === undefined ? undefined : Math.round(ageDays),
      });
      return;
    }

    if (fate === UnpublishReplayFate.KeepAndCountAttempt) {
      await this.countAttemptAsync(doc);
    }
    console.log('FoodUnpublishOutbox: a queued unpublish did not go through, it stays queued',
      doc._id, 'attempts:', doc.attempts, 'reached the server:', serverWasReached, failure);
  }

  /**
   * Record that one more drain attempt reached the server and did not remove the
   * food, so the give-up rule can eventually stop asking.
   *
   * **Never throws**, and that is the whole reason this is its own method rather
   * than a branch inside the item loop. A throw from here would escape the loop,
   * so one item whose local write keeps failing would block every food queued
   * behind it for ever — and the failure would be reported against the drain
   * rather than against the item that caused it. The cost of carrying on instead
   * is that this attempt was not counted, which the next drain simply repeats.
   */
  private async countAttemptAsync(
    doc: UnpublishOutboxDoc & PouchDB.Core.ExistingDocument<UnpublishOutboxDoc>,
  ): Promise<void> {
    doc.attempts += 1;
    try {
      await this.getDb().put(doc);
    } catch (putErr) {
      // expected: a pouch conflict when something removed or rewrote this item
      // while the call was in flight — there is then nothing to count against
      if (isPouchConflict(putErr)) {
        return;
      }
      this.logging.trackSilentProblem('food-unpublish.attempt-count-write-failed', {
        foodId: doc._id,
        attempts: doc.attempts,
        error: describeError(putErr),
      });
    }
  }

  /**
   * Stop retrying one queued removal and say so.
   *
   * Reported at the problem tier, because the condition is unresolved: as far as
   * this device knows, a food is still in the shared food database and nothing
   * will ever ask for it to be taken out again. The report deliberately says
   * that we gave up rather than that the food is definitely still published —
   * the server can refuse for reasons that mean it is already gone, and the
   * client cannot tell those apart without reading the wording of an error
   * string (see food-unpublish-outbox-rules.ts).
   */
  private async dropItemAsync(
    doc: UnpublishOutboxDoc & PouchDB.Core.ExistingDocument<UnpublishOutboxDoc>,
    ageDays: number | undefined,
    why: string,
  ): Promise<void> {
    await this.removeItemAsync(doc);
    this.logging.trackSilentProblem('food-unpublish.replay-given-up', {
      foodId: doc._id,
      isRecipe: doc.isRecipe,
      attempts: doc.attempts,
      ageDays: ageDays === undefined ? undefined : Math.round(ageDays),
      why,
    });
  }

  /**
   * Take one item out of the outbox, tolerating a removal that already happened
   * underneath us.
   *
   * **Never throws**, because both callers are finishing with an item and must go
   * on to the next one. But a failure is not absorbed either: a removal that fails
   * AFTER the server has taken the food out leaves the item queued, and every later
   * drain then asks about a food that is already gone, burns the attempt cap on the
   * server's refusals, and finally reports `replay-given-up` — a problem-tier report
   * claiming a food may still be published when it was removed on the first replay.
   * Nothing else would ever say what really happened, so the local write failure is
   * reported where it occurs.
   */
  private async removeItemAsync(
    doc: UnpublishOutboxDoc & PouchDB.Core.ExistingDocument<UnpublishOutboxDoc>,
  ): Promise<void> {
    try {
      await this.getDb().remove(doc);
    } catch (err) {
      // expected, and genuinely nothing to report: the item has already left the
      // outbox, which is what this method wanted. A conflict means something
      // rewrote it under us, and the next drain settles it again.
      if (isPouchNotFound(err) || isPouchConflict(err)) {
        console.log('FoodUnpublishOutbox: a settled item had already changed or gone', doc._id);
        return;
      }
      this.logging.trackSilentProblem('food-unpublish.outbox-remove-failed', {
        foodId: doc._id,
        attempts: doc.attempts,
        error: describeError(err),
      });
      console.error('FoodUnpublishOutbox: could not take a settled item out of the outbox',
        doc._id, describeErrorForLog(err));
    }
  }
}

/**
 * The value one queued item is sorted by, oldest first, with an unreadable
 * timestamp sorting behind every readable one.
 *
 * `￿` is above every character a valid ISO timestamp can contain, so it
 * puts a damaged document last without needing a second comparator branch.
 */
function sortKeyOf(doc: UnpublishOutboxDoc): string {
  return readQueuedAtIso(doc.createdAt_utc) ?? '￿';
}
